import { useState, useCallback, useEffect, useMemo } from 'react';
import { useCartItems } from '@backpackjs/storefront';

import { useSearchspringBeacon } from '@hooks';

export const useUpsellBeacon = ({
  profileData,
  profileTag,
  visibleProduct,
}) => {
  const cartItems = useCartItems();
  const { sendEventsToBeacon, sendEventsToBeaconV2 } = useSearchspringBeacon();

  const [mounted, setMounted] = useState(false);

  const cartSkus = useMemo(
    () => cartItems?.map((item) => ({ sku: item.variant.sku })) || [],
    [cartItems]
  );

  const sendBeaconEventsOnCartOpen = useCallback(async () => {
    try {
      if (mounted || !profileData?.results || !visibleProduct || !profileTag)
        return;

      await sendEventsToBeacon({
        profileTypes: ['profile.render', 'profile.impression'],
        productTypes: ['profile.product.render', 'profile.product.impression'],
        tag: profileTag,
        placement: 'basket-page',
        products: profileData.results,
        visibleProducts: [visibleProduct],
        seed: cartSkus,
      });
      if (profileData?.responseId) {
        await sendEventsToBeaconV2({
          eventType: 'render',
          responseId: profileData.responseId,
          tag: profileTag,
        });
        await sendEventsToBeaconV2({
          eventType: 'impression',
          responseId: profileData.responseId,
          tag: profileTag,
          results: [
            {
              type: 'product',
              uid: String(visibleProduct?.id ?? visibleProduct?.uid ?? ''),
              parentId: String(
                visibleProduct?.parentId ?? visibleProduct?.id ?? visibleProduct?.uid ?? ''
              ),
            },
          ],
          banners: [],
        });
      }
      setMounted(true);
    } catch (error) {
      console.error(error.message);
    }
  }, [profileData?.results, visibleProduct, profileTag, mounted, cartSkus]);

  const sendBeaconEventsOnClick = useCallback(
    async (event) => {
      try {
        if (!profileTag) return;
        const dataComp = [...(event.target.attributes || [])].find(
          (attr) => attr.name === 'data-comp'
        );
        const productClicked =
          dataComp?.value === 'UpsellAddToCart' ||
          dataComp?.value === 'UpsellSliderItemHeading' ||
          dataComp?.value === 'PictureImage';

        await sendEventsToBeacon({
          profileTypes: ['profile.click'],
          productTypes: productClicked ? ['profile.product.click'] : [],
          tag: profileTag,
          placement: 'basket-page',
          clickedProduct: productClicked ? visibleProduct : null,
          seed: cartSkus,
        });
        if (
          productClicked &&
          visibleProduct &&
          profileData?.responseId
        ) {
          await sendEventsToBeaconV2({
            eventType: 'clickthrough',
            responseId: profileData.responseId,
            tag: profileTag,
            results: [
              {
                type: 'product',
                uid: String(visibleProduct?.id ?? visibleProduct?.uid ?? ''),
                parentId: String(
                  visibleProduct?.parentId ??
                    visibleProduct?.id ??
                    visibleProduct?.uid ??
                    ''
                ),
              },
            ],
          });
        }
      } catch (error) {
        console.error(error.message);
      }
    },
    [profileTag, visibleProduct, cartSkus, profileData?.responseId]
  );

  const sendBeaconEventsOnProductsInView = useCallback(async () => {
    try {
      if (!mounted || !visibleProduct || !profileTag) return;

      await sendEventsToBeacon({
        productTypes: ['profile.product.impression'],
        tag: profileTag,
        placement: 'basket-page',
        visibleProducts: [visibleProduct],
        seed: cartSkus,
      });
      if (profileData?.responseId && visibleProduct) {
        await sendEventsToBeaconV2({
          eventType: 'impression',
          responseId: profileData.responseId,
          tag: profileTag,
          results: [
            {
              type: 'product',
              uid: String(visibleProduct?.id ?? visibleProduct?.uid ?? ''),
              parentId: String(
                visibleProduct?.parentId ??
                  visibleProduct?.id ??
                  visibleProduct?.uid ??
                  ''
              ),
            },
          ],
          banners: [],
        });
      }
    } catch (error) {
      console.error(error.message);
    }
  }, [visibleProduct, profileTag, mounted, cartSkus, profileData?.responseId]);

  useEffect(() => {
    sendBeaconEventsOnCartOpen();
  }, [profileData?.results, visibleProduct, profileTag]);

  useEffect(() => {
    sendBeaconEventsOnProductsInView();
  }, [visibleProduct?.id]);

  return { sendBeaconEventsOnClick };
};
